<template>
    <b-form-group>
        <label>
            <component :is="boldLabel ? 'strong' : 'span'">
                {{ label }}
            </component>
            <HelpPopup v-if="help" :markdown="help">
            </HelpPopup>
        </label>

        <div :class="{ 'd-flex align-items-center': append }" style="gap: 6px;">
            <b-form-textarea v-if="type === 'textarea'" :value="value" @input="$emit('input', $event)" rows="3"
                :maxlength="maxlength" />
            <b-form-input v-else-if="type === 'string'" :value="value" @input="$emit('input', $event)"
                type="text" :maxlength="maxlength" />
            <b-form-input v-else-if="type === 'number'" :value="value" @input="numberInput"
                type="number" :min="min" :max="max" @change="numberChange" clearable />
            <b-checkbox switch v-if="type === 'boolean'" size="lg" :checked="value" @change="$emit('input', $event)" />
            <b-select v-if="type === 'select'" :value="value" :options="options" @change="$emit('input', $event)" />

            <NumberArrayField v-if="type === 'number-array'" :value="value" :add-label="addLabel"
                @input="$emit('input', $event)" />
            <StringArrayField v-if="type === 'string-array'" :value="value" :add-label="addLabel" :maxlength="maxlength"
                :reorderable="reorderable" @input="$emit('input', $event)" />
            <EmailTemplatesField v-if="type === 'email-templates'" :value="value"
                @input="$emit('input', $event)" />
            <TextMessageTemplatesField v-if="type === 'text-message-templates'" :value="value"
                @input="$emit('input', $event)" />
            <PriorAuthReasonsField v-if="type === 'prior-auth-status-reasons'" :value="value"
                :reorderable="reorderable" @input="$emit('input', $event)" />
            <PriorAuthNextTouchField v-if="type === 'prior-auth-next-touch'" :value="value"
                @input="$emit('input', $event)" />
            <DoctorFollowUpNextTouchField v-if="type === 'doctor-follow-up-next-touch'" :value="value"
                @input="$emit('input', $event)" />
            <PAPNextTouchField v-if="type === 'pap-next-touch'" :value="value"
                @input="$emit('input', $event)" />
            <FaxRoutingRuleField v-if="type === 'fax-routing-rule'" :value="value" @input="$emit('input', $event)" />
            <AutoOutboundField v-if="type ==='auto-outbound-configuration'" :value="value"
                @input="$emit('input', $event)" />
            <AutoOutboundStatusField v-if="type === 'auto-outbound-status-configuration'"
                :value="value" @input="$emit('input', $event)" />
            <NameActiveField v-if="type === 'name-active-field'" :value="value" @input="$emit('input', $event)"
                :add-label="addLabel" :locked-ids="lockedIds" :reorderable="reorderable" />
            <TaskTemplateField v-if="type === 'task-template'" :value="value" @input="$emit('input', $event)" />
            <TeamField v-if="type === 'team'" :value="value" @input="$emit('input', $event)" />
            <SpecialitiesField v-if="type === 'specialities'" :value="value" @input="$emit('input', $event)" />
            <EntityAlertConfigurationField v-if="type === 'entity-alert-configuration'" :value="value" @input="$emit('input', $event)" />
            <FaxTemplateField v-if="type === 'fax-templates'" :value="value" @input="$emit('input', $event)" />
            <div v-if="append" style="font-size: 1.2em" class="font-weight-bold">
                {{ append }}
            </div>
        </div>
    </b-form-group>
</template>

<script lang="ts">
// ConfigurationField.ts
import { Component, Prop, Vue } from 'vue-property-decorator';
import TextMessageTemplatesField from "@/components/Configuration/TextMessageTemplatesField.vue";
import NameActiveField from "@/components/Configuration/NameActiveField.vue";
import StringArrayField from "@/components/Configuration/StringArrayField.vue";
import PriorAuthReasonsField from "@/components/Configuration/PriorAuthReasonsField.vue";
import PriorAuthNextTouchField from "@/components/Configuration/PriorAuthNextTouchField.vue";
import HelpPopup from "@/components/HelpPopup.vue";
import DoctorFollowUpNextTouchField from "@/components/Configuration/DoctorFollowUpNextTouchField.vue";
import PAPNextTouchField from "@/components/Configuration/PAPNextTouchField.vue";
import NumberArrayField from "@/components/Configuration/NumberArrayField.vue";
import TaskTemplateField from "./TaskTemplateField.vue";
import FaxRoutingRuleField from "@/components/Configuration/FaxRoutingRuleField.vue";
import SpecialitiesField from "./SpecialitiesField.vue";
import EmailTemplatesField from "@/components/Configuration/EmailTemplatesField.vue";
import AutoOutboundStatusField from "@/components/Configuration/AutoOutboundStatusField.vue";
import AutoOutboundField from "@/components/Configuration/AutoOutboundField.vue";
import TeamField from "@/components/Configuration/TeamField.vue";
import EntityAlertConfigurationField from "@/components/Configuration/EntityAlertConfigurationField.vue";
import FaxTemplateField from "@/components/Configuration/FaxTemplateField.vue";

@Component({
    name: "ConfigurationField",
    components: {
        EmailTemplatesField,
        AutoOutboundField,
        AutoOutboundStatusField,
        FaxRoutingRuleField,
        NumberArrayField,
        DoctorFollowUpNextTouchField,
        PAPNextTouchField,
        HelpPopup,
        TextMessageTemplatesField,
        NameActiveField,
        StringArrayField,
        PriorAuthReasonsField,
        PriorAuthNextTouchField,
        TaskTemplateField,
        SpecialitiesField,
        TeamField,
        EntityAlertConfigurationField,
        FaxTemplateField
    },
})
export default class ConfigurationField extends Vue {
    @Prop({ type: String }) label!: string;
    @Prop({ type: Boolean }) boldLabel!: boolean;
    @Prop({ type: String }) help!: string;
    @Prop({ type: String }) type!: string;
    @Prop() value!: any;
    @Prop({ type: String }) addLabel!: string;
    @Prop({
        type: Array,
        default: () => [],
    }) lockedIds!: Array<any>;
    @Prop({
        type: Array,
        required: false,
    }) options?: Array<any>;
    @Prop({ type: String }) append!: string;
    @Prop({ type: Number }) maxlength!: number;
    @Prop({ type: Number }) min!: number;
    @Prop({ type: Number }) max!: number;
    @Prop({ type: Boolean, default: false }) reorderable!: boolean;

    numberInput(val: string | number) {
        if (val === '' || val === null || val === undefined) {
            this.$emit('input', null);
            return;
        }
        const numVal = typeof val === 'string' ? parseFloat(val) : val;
        if (isNaN(numVal)) {
            this.$emit('input', null);
            return;
        }
        this.$emit('input', numVal);
    }

    numberChange(val: number) {
        if (!isNaN(this.min) && val < this.min) {
            this.$emit('input', this.min);
            return;
        }
        if (!isNaN(this.max) && val > this.max) {
            this.$emit('input', this.max);
            return;
        }
    }
}

</script>
